import React, { FC, useMemo, useRef, useState } from 'react'
import { AlertDialog, AlertDialogContent, AlertDialogOverlay, Button, VStack } from '@chakra-ui/react'
import { Icon } from '../icon'
import { Scrollbars } from 'rc-scrollbars'
import { Heading } from '../typography'

export interface Option {
  value: string
  label: string
}

export interface SelectProps {
  options: Option[]
  value?: string
  placeholder?: string
  onChange?: (values: string[]) => void
}

export const Select: FC<SelectProps> = ({ options, placeholder, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const onClose = () => setIsOpen(false)
  const cancelRef = useRef<HTMLElement>(null)
  const label = useMemo(
    () => options.find(({ value: option }) => option === value)?.label || placeholder,
    [options, placeholder, value],
  )

  return (
    <>
      <Button
        fontWeight="400"
        padding={6}
        rightIcon={<Icon icon="chevron-down" color="green.500" />}
        border={2}
        borderStyle="solid"
        borderColor="grey"
        variant="primary"
        height={85}
        width={[null, '100%']}
        minWidth={270}
        maxWidth={460}
        margin="2vh"
        justifyContent="space-between"
        borderRadius="lg"
        onClick={() => setIsOpen(true)}
        role="combobox"
        _hover={{
          borderColor: 'purple',
        }}
        marginTop={7}
      >
        <Heading level={3}>{label}</Heading>
      </Button>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent padding={4} borderRadius="2xl" maxWidth={340} margin="0">
            <Scrollbars style={{ width: '100%' }} autoHeight autoHeightMax={400}>
              <VStack spacing={0} margin={2}>
                {options.map(({ label, value: option }) => (
                  <Button
                    key={`option-${option}`}
                    role="option"
                    height={10}
                    paddingX={5}
                    borderRadius="lg"
                    isActive={option === value}
                    fontSize="xxs"
                    width="full"
                    margin="0"
                    border="none"
                    justifyContent="left"
                    onClick={() => {
                      setIsOpen(false)
                      onChange && onChange([option])
                    }}
                    _hover={{
                      color: 'purple',
                      backgroundColor: 'purpleAlpha',
                    }}
                    _active={{
                      color: 'purple',
                      backgroundColor: 'purpleAlpha',
                    }}
                    _focus={{
                      borderColor: 'brand',
                      boxShadow: '0 0 0 2px #5bce4c',
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </VStack>
            </Scrollbars>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
